import { Injectable } from '@angular/core';
import { BehaviorSubject, map } from 'rxjs';
import { ChatMemberDto } from '@src/api-messenger';

import { UpdateUserModel, UserModel } from '../types';
import { ChatMemberModel, UpdateChatMemberModel, ChatModel, ChatPermissionsModel, UpdateChatModel } from '../chat';
import { MessageModel, MessageFooterStoreModel } from '../message';
import { ReactionType } from '../reaction';

@Injectable({
  providedIn: 'root',
})
export class StoreService {
  users$ = new BehaviorSubject<Record<string, UserModel>>({});
  chats$ = new BehaviorSubject<Record<string, ChatModel>>({});
  attachedChats$ = new BehaviorSubject<Record<string, string[] | undefined>>({});
  chatPermissions$ = new BehaviorSubject<Record<string, ChatPermissionsModel>>({});
  chatMessages$ = new BehaviorSubject<Record<string, Record<string, MessageModel>>>({});
  chatMembers$ = new BehaviorSubject<Record<string, Record<string, ChatMemberModel>>>({});
  messages$ = new BehaviorSubject<Record<string, MessageModel>>({});
  footerMessages$ = new BehaviorSubject<Record<string, MessageFooterStoreModel | undefined>>({});
  draftMessages$ = new BehaviorSubject<Record<string, string>>({});

  getChat(chatId: string) {
    return this.chats$.value?.[chatId];
  }

  getAttachedChats(attachId?: string) {
    return this.chats$.pipe(
      map(chats => {
        if (!attachId) {
          return [];
        } else {
          return Object.entries(chats)
            .filter(([chatId, _]) => {
              return this.attachedChats$.value[attachId]?.includes(chatId);
            })
            .map(([_, chat]) => chat);
        }
      }),
    );
  }

  setChatsByAttachId(attachId: string, chats: ChatModel[]) {
    const attachedChats = this.attachedChats$.value;
    attachedChats[attachId] = chats.map(chat => chat.chatId);
    this.attachedChats$.next(attachedChats);

    this.updateChatsState(chats);
  }

  clearStore() {
    this.users$.next({});
    this.chats$.next({});
    this.attachedChats$.next({});
    this.chatPermissions$.next({});
    this.chatMessages$.next({});
    this.chatMembers$.next({});
    this.messages$.next({});
    this.footerMessages$.next({});
    this.draftMessages$.next({});
  }

  setMembersState(members: ChatMemberDto[]) {
    if (members.length) {
      members.forEach(member => {
        this.setMemberState(member);
      });
    }
  }

  setMemberState(member: ChatMemberDto, online: boolean = false) {
    this.setUserState({
      userId: member.userId,
      userName: member.userName,
      online,
    });
  }

  setUserState(user: UserModel) {
    const users = this.users$.value;
    users[user.userId] = user;
    this.users$.next(users);
  }

  updateUserState(user: UpdateUserModel) {
    const users = this.users$.value;
    if (user && user.userId) {
      const oldUser = users[user.userId];
      users[user.userId] = {
        ...oldUser,
        ...user,
      };
    }
    this.users$.next(users);
  }

  getUser(userId: string) {
    return this.users$.value[userId];
  }

  updateChatsState(chats: UpdateChatModel[]) {
    if (chats.length) {
      chats.forEach(chat => {
        if (chat.chatId) {
          this.updateChatState(chat.chatId, chat);
        }
      });
    }
  }

  private updateChatState(chatId: string, chat?: UpdateChatModel) {
    const chats = this.chats$.value;
    if (chat) {
      const oldChat = chats[chatId];
      chats[chatId] = {
        ...oldChat,
        ...chat,
      };
    }
    this.chats$.next(chats);
  }

  addChatByAttachId(attachId: string, chat: ChatModel) {
    const chatId = chat.chatId;
    const attachedChats = this.attachedChats$.value;
    attachedChats[attachId]?.push(chatId);
    this.attachedChats$.next(attachedChats);

    this.updateChatState(chatId, chat);
  }

  deleteChatByAttachId(attachId: string, chatId: string) {
    const attachedChats = this.attachedChats$.value;
    attachedChats[attachId] = attachedChats[attachId]?.filter(attachedChatId => attachedChatId !== chatId);
    this.attachedChats$.next(attachedChats);

    this.deleteChat(chatId);
  }

  private deleteChat(chatId: string) {
    const chats = this.chats$.value;
    delete chats[chatId];
    this.chats$.next(chats);

    this.deleteChatMessages(chatId);
  }

  // =============================================

  setChatPermissionsState(chatId: string, permissions: ChatPermissionsModel) {
    const chatPermissions = this.chatPermissions$.value;
    chatPermissions[chatId] = permissions;
    this.chatPermissions$.next(chatPermissions);
  }

  getChatPermissionsState(chatId: string) {
    return this.chatPermissions$.value[chatId];
  }

  // =============================================

  private setMessageState(message: MessageModel) {
    const messages = this.messages$.value;
    messages[message.id] = message;
    this.messages$.next(messages);
  }

  getMessage(messageId: string) {
    return this.messages$.value[messageId];
  }

  deleteMessage(messageId: string) {
    const messages = this.messages$.value;
    delete messages[messageId];
    this.messages$.next(messages);
  }

  // =============================================

  setChatMessagesState(chatId: string, messages: MessageModel[]) {
    if (messages.length) {
      messages.forEach(message => {
        this.setChatMessageState(chatId, message);
        this.setMessageState(message);
      });
    } else {
      this.setChatMessageState(chatId);
    }
  }

  updateUserNameForChatMessagesState(chatId: string, user: UpdateUserModel) {
    const { userId, userName } = user;

    const chatMessages = this.chatMessages$.value;

    if (userName) {
      const messages = chatMessages[chatId];
      if (messages) {
        Object.keys(messages).forEach(messageId => {
          const oldMessage = messages[messageId];
          if (oldMessage.userId === userId) {
            messages[messageId] = {
              ...oldMessage,
              userName,
            };
          }
        });
      }

      chatMessages[chatId] = messages;
    }

    this.chatMessages$.next(chatMessages);
  }

  private setChatMessageState(chatId: string, message?: MessageModel) {
    const chatMessages = this.chatMessages$.value;
    if (!chatMessages[chatId]) {
      chatMessages[chatId] = {};
    }
    if (message) {
      chatMessages[chatId][message.id] = message;
    }
    this.chatMessages$.next(chatMessages);
  }

  getChatMessage(chatId: string, messageId: string) {
    return this.chatMessages$.value[chatId][messageId];
  }

  deleteChatMessage(chatId: string, messageId: string) {
    const chatMessages = this.chatMessages$.value;
    delete chatMessages[chatId][messageId];
    this.chatMessages$.next(chatMessages);
  }

  deleteChatMessages(chatId: string) {
    const chatMessages = this.chatMessages$.value;
    delete chatMessages[chatId];
    this.chatMessages$.next(chatMessages);
  }

  // =============================================

  setMessageReactionsState(chatId: string, messageId: string, reactions: ReactionType[]) {
    const chatMessages = this.chatMessages$.value;
    if (!chatMessages[chatId]) {
      chatMessages[chatId] = {};
    }

    chatMessages[chatId][messageId].reactions = reactions;
    this.chatMessages$.next(chatMessages);
  }

  // =============================================

  setChatMembersState(chatId: string, members: ChatMemberModel[]) {
    if (members.length) {
      members.forEach(member => {
        this.setChatMemberState(chatId, member);
        this.setUserState(member);
      });
    } else {
      this.setChatMemberState(chatId);
    }
  }

  private setChatMemberState(chatId: string, member?: ChatMemberModel) {
    const chatMembers = this.chatMembers$.value;
    if (!chatMembers[chatId]) {
      chatMembers[chatId] = {};
    }
    if (member) {
      chatMembers[chatId][member.userId] = member;
    }
    this.chatMembers$.next(chatMembers);
  }

  updateChatMembersState(chatId: string, members: UpdateChatMemberModel[]) {
    if (members.length) {
      members.forEach(member => {
        this.updateChatMemberState(chatId, member);
        this.updateUserState(member);
      });
    } else {
      this.updateChatMemberState(chatId);
    }
  }

  private updateChatMemberState(chatId: string, member?: UpdateChatMemberModel) {
    const chatMembers = this.chatMembers$.value;
    if (!chatMembers[chatId]) {
      chatMembers[chatId] = {};
    }
    if (member && member.userId) {
      const oldMember = chatMembers[chatId][member.userId];
      chatMembers[chatId][member.userId] = {
        ...oldMember,
        ...member,
      };
    }
    this.chatMembers$.next(chatMembers);
  }

  getChatMembers(chatId: string) {
    return this.chatMembers$.value[chatId];
  }

  getChatMember(chatId: string, memberId: string) {
    return this.chatMembers$.value[chatId][memberId];
  }

  deleteChatMember(chatId: string, memberId: string) {
    const chatMembers = this.chatMembers$.value;

    if (chatMembers[chatId] && chatMembers[chatId][memberId]) {
      delete chatMembers[chatId][memberId];
      this.chatMembers$.next(chatMembers);
    }
  }

  // =============================================

  setFooterMessageState(chatId: string, data: MessageFooterStoreModel) {
    const footerMessages = this.footerMessages$.value;
    footerMessages[chatId] = data;
    this.footerMessages$.next(footerMessages);
  }

  getFooterMessage(chatId: string) {
    return this.footerMessages$.value[chatId];
  }

  deleteFooterMessage(chatId: string) {
    const footerMessages = this.footerMessages$.value;
    delete footerMessages[chatId];
    this.footerMessages$.next(footerMessages);
  }

  setDraftMessageState(chatId: string, draftMessage: string) {
    const draftMessages = this.draftMessages$.value;
    draftMessages[chatId] = draftMessage;
    this.draftMessages$.next(draftMessages);
  }

  getDraftMessage(chatId: string) {
    return this.draftMessages$.value[chatId];
  }

  deleteDraftMessage(chatId: string) {
    const draftMessages = this.draftMessages$.value;
    delete draftMessages[chatId];
    this.draftMessages$.next(draftMessages);
  }
}
